import { DateField } from '../../components/ui/date-field';
import { useEffect, useState } from 'react';
import { api, errorLine } from '../../api/client';
import type { AdminOrg, AdminUsage } from '../../api/types';
import {
  monthlyLimitField,
  monthlyLimitFrom,
  monthlyLimitSaveWithheld,
  monthlyLimitUnusable,
} from '../../lib/budget';
import { formatMoney } from '../../lib/format';
import {
  atTheMonthlyLimit,
  CONNECTOR_CAPTION,
  CONNECTOR_HEADING,
  CONNECTOR_TOOL_HEADING,
  CONNECTOR_USER_HEADING,
  connectorTotal,
  modelRows,
  MONTHLY_LIMIT_AT_THE_LIMIT,
  MONTHLY_LIMIT_AT_THE_LIMIT_DETAIL,
  MONTHLY_LIMIT_HELP,
  MONTHLY_LIMIT_INVALID_NOTE,
  MONTHLY_LIMIT_LABEL,
  MONTHLY_LIMIT_NONE,
  monthlyLimitLine,
  sourceRows,
  stageRows,
  toolRows,
  userRows,
  type BreakdownRow,
} from '../../lib/usage';

/**
 * Spend for the selected organization, and what it is allowed to be (§9,
 * amended 2026-08-27; the allowance 2026-09-09). Spend is admin-plane only: the
 * office dashboard used to carry this in its foot and no longer shows cost
 * anywhere, so this is the surface it is watched from.
 *
 * **"Monitored, not capped" stopped being true on 2026-09-09.** It was the rule
 * for the whole of this panel's life and it is worth saying what replaced it
 * rather than deleting it: there is now a monthly allowance, the server refuses
 * new work at it, and {@link MonthlySpendLimitForm} sits directly over the
 * rollup because the number it sets is the number under it. The *breakdown*
 * below is still monitored and not capped — no threshold, no warning colour; if
 * a line in it looks wrong, that is a conversation.
 *
 * The per-run limit is a different knob and stays where it was, with the three
 * settings that decide what one run costs: see `routes/org/research.tsx`.
 *
 * A failed rollup is silence rather than a flag — a spend line that could not
 * load must not look like the tab, or the organization behind it, failed to open
 * — but the **control** renders either way, because this tab is entered to set
 * the limit as often as to read the numbers, and a read that failed must not
 * take the only door to the limit down with it.
 */
export function Spend({ org, onSaved }: { org: AdminOrg; onSaved?: (org: AdminOrg) => void }) {
  const [usage, setUsage] = useState<AdminUsage | null>(null);
  /* Bumped by a saved limit. The rollup's numbers do not move when a limit is
     written, but "of $X" and the flag over it both do, and both are the
     server's answers rather than this app's arithmetic — so the read is made
     again rather than patched in place. */
  const [reload, setReload] = useState(0);
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));

  useEffect(() => {
    let live = true;
    api
      .usage(org.id, month)
      .then((answer) => {
        if (live) setUsage(answer);
      })
      .catch(() => {
        if (live) setUsage(null);
      });
    return () => {
      live = false;
    };
  }, [org.id, reload, month]);

  const limited = monthlyLimitLine(usage ?? ({} as AdminUsage));
  return (
    <div className="credit-ledger spend-page">
      <header className="credit-page-head">
        <div>
          <div className="eyebrow">Usage & costs</div>
          <h2>Spend</h2>
        </div>
      </header>
      <p className="field-note">
        Recorded provider estimates and operational limits. Dollar costs are separate from customer
        credits.
      </p>
      <section className="credit-card">
        <h3>Monthly allowance</h3>
        {/* The control comes first, and it renders whether or not the rollup
          loaded: a spend read that failed must not take the only door to the
          limit down with it. */}
        <MonthlySpendLimitForm
          org={org}
          atTheLimit={atTheMonthlyLimit(usage)}
          onSaved={(saved) => {
            setReload((n) => n + 1);
            onSaved?.(saved);
          }}
        />
      </section>
      <section className="credit-card spend-overview">
        <div className="credit-card-head">
          <h3>Period overview</h3>
          <DateField
            id="spend-period"
            label="Spend period (UTC)"
            mode="month"
            value={month}
            onChange={(value) => {
              setUsage(null);
              setMonth(value);
            }}
          />
        </div>
        {usage ? (
          <>
            <div className="spend-total">
              {limited ?? `Research spend this month: ${formatMoney(usage.total_micro_usd)}`}
            </div>
            <div className="admin-note">
              {usage.reporting === undefined
                ? 'Usage reporting status is unavailable.'
                : usage.reporting.pending_reports
                  ? `${usage.reporting.pending_reports} usage reports are awaiting cost recording (${usage.reporting.failed_reports} reports with recording failures). Spend may be understated; reopening this page retries recording.`
                  : 'No received usage reports are awaiting cost recording. This does not verify usage that was never reported, including legacy workers.'}
            </div>
            {usage.reporting && usage.reporting.invocations_without_final_report > 0 ? (
              <div className="admin-note">
                {usage.reporting.invocations_without_final_report} research sessions have no final
                usage report (including ongoing research and older worker versions). Their usage
                completeness is unknown.
              </div>
            ) : null}
          </>
        ) : (
          <p className="field-note">Spend data is loading or unavailable for this period.</p>
        )}
      </section>
      {usage && (
        <>
          <OperationSpend usage={usage} />
          <section className="credit-card">
            <h3>Spend breakdown</h3>
            <p className="field-note">
              The same recorded model spend, grouped three ways. These tables are not additive.
            </p>
            <SpendBreakdown usage={usage} />
          </section>
          <ConnectorCalls usage={usage} />
        </>
      )}
    </div>
  );
}

/**
 * **What the whole organization may spend in a calendar month** (2026-09-09) —
 * the one control on this page that stops something rather than describing it.
 *
 * It sits on this tab with the rollup rather than on the Research tab with the
 * four knobs there (`routes/org/research.tsx`), because it is not one of them:
 * those price a research run, and this caps
 * everything the organization spends — a run, a note read on a record, an
 * identity match, an imported file. The number it is compared against is the
 * number directly under it, which is the whole reason the two are together.
 *
 * The affordances are the per-run field's, deliberately: a text field where
 * blank is "no limit", an error border and a line saying what to write when what
 * is written is not an amount, and a Save offered only by a change. What it does
 * **not** share is the four-control bag — this posts the limit alone, on a door
 * where naming a key writes it and not naming it leaves it alone, so the two
 * panels cannot clear each other.
 *
 * `atTheLimit` is the server's own answer off the usage read, never a comparison
 * made here: a panel that decided for itself could say "under" about an
 * organization every door is refusing.
 */
export function MonthlySpendLimitForm({
  org,
  atTheLimit,
  onSaved,
}: {
  org: AdminOrg;
  atTheLimit: boolean;
  onSaved?: (org: AdminOrg) => void;
}) {
  const [saved, setSaved] = useState(() => monthlyLimitField(org.monthly_spend_ceiling_usd));
  const [draft, setDraft] = useState(saved);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const unusable = monthlyLimitUnusable(draft);
  const withheld = monthlyLimitSaveWithheld({ draft, saved, saving });

  const save = async () => {
    if (withheld) return;
    setSaving(true);
    try {
      const answer = await api.updateMonthlySpendLimit(org.id, monthlyLimitFrom(draft));
      const settled = monthlyLimitField(answer.org.monthly_spend_ceiling_usd);
      setSaved(settled);
      setDraft(settled);
      setError(null);
      onSaved?.(answer.org);
    } catch (caught) {
      setError(errorLine(caught));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="form monthly-limit">
      {atTheLimit ? (
        <div className="flag stopped row">
          <div className="label">{MONTHLY_LIMIT_AT_THE_LIMIT}</div>
          <div className="detail">{MONTHLY_LIMIT_AT_THE_LIMIT_DETAIL}</div>
        </div>
      ) : null}

      <div className={unusable ? 'field over' : 'field'}>
        <label className="label" htmlFor="org-monthly-limit">
          {MONTHLY_LIMIT_LABEL}
        </label>
        <input
          id="org-monthly-limit"
          type="text"
          inputMode="decimal"
          value={draft}
          disabled={saving}
          aria-invalid={unusable || undefined}
          aria-describedby={unusable ? 'org-monthly-limit-note' : undefined}
          onChange={(event) => setDraft(event.target.value)}
        />
        {draft.trim().length === 0 ? <div className="field-note">{MONTHLY_LIMIT_NONE}</div> : null}
        {unusable ? (
          <div id="org-monthly-limit-note" className="field-note over-note">
            {MONTHLY_LIMIT_INVALID_NOTE}
          </div>
        ) : null}
        <div className="field-note">{MONTHLY_LIMIT_HELP}</div>
      </div>

      {error ? (
        <div className="flag row">
          <div className="label">Error</div>
          <div className="detail">{error}</div>
        </div>
      ) : null}

      <div className="form-actions">
        <button type="button" className="btn go" disabled={withheld} onClick={() => void save()}>
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}

/**
 * **What the organization's Claude workspace asked of the system this month**
 * (2026-09-07) — two tables under the spend, and the one caption on this page
 * that says a number is deliberately absent.
 *
 * It comes off the same response as the spend and is drawn under it because the
 * questions are next to each other: an operator watching what an organization
 * costs is the same person wondering whether anybody is using the connector at
 * all. What it must never do is read as a fourth spend table, which is what the
 * caption is for.
 *
 * Silent on a month with no calls, exactly as `SpendBreakdown` is: an empty
 * table under a caption explaining that it has no prices is noise on every
 * organization that has not connected a workspace, which today is all of them.
 */
export function ConnectorCalls({ usage }: { usage: AdminUsage }) {
  const total = connectorTotal(usage);
  if (!total) return null;
  const tools = toolRows(usage);
  const callers = userRows(usage);
  return (
    <section className="credit-card">
      <h3>Connector activity</h3>
      <div className="admin-note">
        {CONNECTOR_HEADING}: {total} {CONNECTOR_CAPTION}
      </div>
      <div className="spend-breakdown">
        <table className="table spend-table">
          <thead>
            <tr>
              <th>{CONNECTOR_TOOL_HEADING}</th>
              <th className="numeric">Calls</th>
              <th className="numeric">Refused</th>
              <th className="numeric">Median</th>
            </tr>
          </thead>
          <tbody>
            {tools.map((row) => (
              <tr key={row.key}>
                <td>{row.tool}</td>
                <td className="numeric">{row.calls}</td>
                <td className="numeric">{row.errors}</td>
                <td className="numeric">{row.median}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <table className="table spend-table">
          <thead>
            <tr>
              <th>{CONNECTOR_USER_HEADING}</th>
              <th className="numeric">Calls</th>
            </tr>
          </thead>
          <tbody>
            {callers.map((row) => (
              <tr key={row.key}>
                <td>{row.email}</td>
                <td className="numeric">{row.calls}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/**
 * Where the month's spend went — two small tables under the total (§9,
 * 2026-09-03).
 *
 * It answers the questions the single number could not: which round, which
 * facet, which analysis kind. Rows an engine did not break down, and the
 * server's own calls, share one honest line rather than being guessed at or
 * dropped — each table groups the same rows a different way, so each adds up to
 * the total above it.
 *
 * Silent when there is nothing to break down. A month with no spend and a
 * response from a server that does not send the field read the same way here,
 * which is correct: in both cases there is nothing to show.
 */
export function SpendBreakdown({ usage }: { usage: AdminUsage }) {
  const stages = stageRows(usage);
  const models = modelRows(usage);
  const sources = sourceRows(usage);
  if (stages.length === 0 && models.length === 0 && sources.length === 0) return null;
  return (
    <div className="spend-breakdown">
      <BreakdownTable heading="What it was for" rows={sources} />
      <BreakdownTable heading="Where it went" rows={stages} />
      <BreakdownTable heading="By model" rows={models} />
    </div>
  );
}

export function BreakdownTable({ heading, rows }: { heading: string; rows: BreakdownRow[] }) {
  if (rows.length === 0) return null;
  return (
    <table className="table spend-table">
      <thead>
        <tr>
          <th>{heading}</th>
          <th className="numeric">Spend</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.key}>
            <td>{row.label}</td>
            <td className="numeric">{row.amount}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** Download only the aggregate report: MCP user traffic and research content are excluded. */
export function OperationSpend({ usage }: { usage: AdminUsage }) {
  const report = usage.operations;
  if (!report) return <p>Operation-level usage is unavailable.</p>;
  const download = () => {
    const blob = new Blob([JSON.stringify({ month: usage.month, ...report }, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `operation-costs-${usage.month}.json`;
    anchor.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  return (
    <section className="credit-card spend-economics">
      <h3>Operation economics</h3>
      <p>
        Recorded model estimates: {formatMoney(report.model_micro_usd)}. Rated external costs:{' '}
        {formatMoney(report.external_micro_usd)}. {report.unrated_external_attempts} external
        requests have unknown billable units and cost; they are not free requests.
      </p>
      <p>
        {report.completed} completed operations in this period. Recorded cost per completed
        operation, including failed and unfinished work:{' '}
        {report.micro_usd_per_completed === null
          ? 'Not available — no completions'
          : formatMoney(report.micro_usd_per_completed)}
        .
      </p>
      <p>
        {report.incomplete_attempts} model attempts have partial, unknown, or aggregate usage.{' '}
        {report.pending_model_projections} model attempts await cost recording (all periods).{' '}
        {report.legacy_pricing_events} events have legacy pricing; {report.fallback_pricing_events}{' '}
        use a fallback estimate.
      </p>
      <div
        className="spend-table-scroll"
        tabIndex={0}
        role="region"
        aria-label="Operation economics table"
      >
        <table className="table spend-table">
          <thead>
            <tr>
              <th>Work / depth</th>
              <th>Current outcome</th>
              <th>Operations</th>
              <th>Completed</th>
              <th>No period usage</th>
              <th>Model attempts*</th>
              <th>External requests</th>
              <th>Recorded cost</th>
              <th>Cost per completion*</th>
            </tr>
          </thead>
          <tbody>
            {report.rows.map((r, i) => (
              <tr key={i}>
                <td>
                  {r.kind} / {r.depth}
                </td>
                <td>{r.outcome}</td>
                <td>{r.operations}</td>
                <td>{r.completed}</td>
                <td>{r.without_usage}</td>
                <td>{r.model_attempts}</td>
                <td>{r.external_attempts}</td>
                <td>{formatMoney(r.model_micro_usd + r.external_micro_usd)}</td>
                <td>
                  {r.micro_usd_per_completed === null
                    ? '—'
                    : formatMoney(r.micro_usd_per_completed)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <details>
        <summary>How these figures are calculated</summary>
        <p>
          *Unit costs pool all outcomes for the same work kind and depth. Legacy aggregate events
          are a lower bound on attempt counts. Costs use the period of recorded usage; completions
          use completion time. Outcomes reflect current state. Import completion time uses the
          session’s last update.
        </p>
        <p>
          Unallocated recorded cost: {formatMoney(report.unallocated_micro_usd)}. Provider invoices
          have not been reconciled; unexplained variance is unknown. Cloud runtime, storage,
          support, and unreported provider usage remain outside these estimates.
        </p>
      </details>
      <div className="form-actions">
        <button className="btn" type="button" onClick={download}>
          Export aggregate costs
        </button>
      </div>
    </section>
  );
}
